// Aba "Painel" — equivale à aba Resumo da planilha: baseline (a), ajuste (b),
// meta (c) e realizado (d) por mês e trimestre, por setor, com as variações
// b×a, b×c, d×b e d×c, o acumulado contra a meta anual e a precisão da previsão.
import { useEffect, useMemo, useState } from 'react';
import { App as AntApp, Card, Col, Empty, Flex, Row, Segmented, Select, Spin, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, LabelList, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import * as api from './api';
import type { Agregado, MesPainel, Painel } from './api';
import { brl, brlCurto, MESES, MESES_CURTOS, ORDEM_SETORES, pct, SERIE_COR, SETOR_COR, SETOR_CURTO, SETOR_NOMES } from './regras';

const { Text } = Typography;
const ALTURA = 'clamp(240px, calc(100vh - 520px), 380px)';
const EIXO = { fontSize: 12, fill: '#898781' };

type Linha = (MesPainel | Agregado) & { chave: string; tipo: 'mes' | 'tri' | 'total' };

// "Entregue × meta mensal": acima/abaixo da meta é estado (cor de status), sempre
// acompanhado do número com sinal — nunca só a cor.
const COR_ACIMA = '#0ca30c';
const COR_ABAIXO = '#d03b3b';
const fmtVar = (v: number) => `${v > 0 ? '+' : ''}${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

// Rótulo da barra: em cima da barra positiva, embaixo da negativa.
function RotuloVariacao({ x, y, width, height, value }: any) {
  if (value == null || x == null) return null;
  const topo = Math.min(y, y + height), base = Math.max(y, y + height);
  return (
    <text x={x + width / 2} y={value >= 0 ? topo - 6 : base + 13} textAnchor="middle" fontSize={11} fontWeight={600} fill="#0b0b0b">
      {fmtVar(Number(value))}
    </text>
  );
}

// Eixo X com o mês e, embaixo, a meta do mês.
function TickMesMeta({ x, y, payload, index, dados }: any) {
  const meta = dados?.[index]?.meta;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fontSize={12} fill="#52514e">{payload?.value}</text>
      <text dy={26} textAnchor="middle" fontSize={10} fill="#898781">{meta != null ? brlCurto(meta).replace('R$ ', '') : '—'}</text>
    </g>
  );
}

function TooltipVariacao({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <div style={{ background: '#fff', border: '1px solid #ecebe8', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div>Entregue: <b>{brl(d.entregue)}</b></div>
      <div>Meta mensal: {brl(d.meta)}</div>
      <div>Variação: <span style={{ color: d.variacao == null ? undefined : d.variacao >= 0 ? COR_ACIMA : COR_ABAIXO, fontWeight: 600 }}>
        {d.variacao == null ? '—' : fmtVar(d.variacao)}</span>
        {d.variacao != null && <span> ({d.variacao >= 0 ? 'acima' : 'abaixo'} da meta)</span>}</div>
    </div>
  );
}

// Tooltip do "Realizado por setor": setores, total entregue, meta e a variação.
function TooltipSetor({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  const barras = payload.filter((x: any) => x.dataKey !== 'meta' && x.value != null);
  return (
    <div style={{ background: '#fff', border: '1px solid #ecebe8', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {barras.map((x: any) => (
        <div key={x.dataKey}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: x.color, marginRight: 6 }} />{x.name}: {brl(x.value)}</div>
      ))}
      <div style={{ marginTop: 4 }}>Entregue: <b>{brl(d.entregue)}</b></div>
      <div>Meta: {brl(d.meta)}</div>
      <div>Variação sobre a meta: <span style={{ color: corVar(d.variacao) }}>{d.variacao == null ? '—' : `${d.variacao > 0 ? '+' : ''}${pct(d.variacao)}`}</span></div>
    </div>
  );
}

const corVar = (v: number | null) => (v == null ? undefined : v >= 0 ? '#0ca30c' : '#d03b3b');
const Var = ({ v }: { v: number | null }) => <span style={{ color: corVar(v) }}>{v == null ? '—' : `${v > 0 ? '+' : ''}${pct(v, 2)}`}</span>;

export default function AbaPainel() {
  const { message } = AntApp.useApp();
  const [ano, setAno] = useState(new Date().getFullYear());
  const [p, setP] = useState<Painel | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [visao, setVisao] = useState<'graficos' | 'tabela'>('graficos');

  useEffect(() => {
    setCarregando(true);
    api.obterPainel(ano).then(setP).catch(e => message.error(`Não carregou o painel: ${api.erroDe(e)}`)).finally(() => setCarregando(false));
  }, [ano, message]);

  const setores = useMemo(() => ORDEM_SETORES.filter(s => s !== 'sem_setor'
    || (p && Object.keys(p.total.realizado_por_setor || {}).includes('sem_setor'))), [p]);

  const linhas: Linha[] = useMemo(() => {
    if (!p) return [];
    const out: Linha[] = [{ ...p.total, chave: 'total', tipo: 'total' }];
    p.trimestres.forEach((t, i) => {
      out.push({ ...t, chave: `t${i}`, tipo: 'tri' });
      p.meses.slice(i * 3, i * 3 + 3).forEach(m => out.push({ ...m, chave: `m${m.mes}`, tipo: 'mes' }));
    });
    return out;
  }, [p]);

  const dadosMensais = (p?.meses || []).map(m => ({
    nome: MESES_CURTOS[m.mes - 1], baseline: m.baseline, ajuste: m.ajuste, realizado: m.realizado, meta: m.meta,
    ...Object.fromEntries(setores.map(s => [s, m.realizado_por_setor?.[s] ?? null])),
  }));

  // Filtro de setores do gráfico "Realizado por setor". Vazio = todos.
  const [filtroSetores, setFiltroSetores] = useState<string[]>([]);
  const selecionados = filtroSetores.length ? setores.filter(s => filtroSetores.includes(s)) : setores;
  const todosSelecionados = selecionados.length === setores.filter(s => s !== 'sem_setor').length
    && setores.filter(s => s !== 'sem_setor').every(s => selecionados.includes(s));
  // Meta da seleção: todos os setores = meta geral do mês; um subconjunto = soma das metas
  // por setor (só quando TODOS os escolhidos têm meta própria; senão não há meta comparável).
  const metaDaSelecao = (m: MesPainel): number | null => {
    if (todosSelecionados) return m.meta;
    const doSetor = selecionados.map(s => m.meta_por_setor?.[s]);
    return doSetor.every(v => v != null) ? doSetor.reduce((a, b) => a + (b || 0), 0) : null;
  };
  const dadosSetor = (p?.meses || []).map(m => {
    const temRealizado = m.realizado != null;
    const soma = selecionados.reduce((a, s) => a + (m.realizado_por_setor?.[s] || 0), 0);
    const meta = metaDaSelecao(m);
    const variacao = temRealizado && meta ? Math.round(((soma / meta) - 1) * 1000) / 10 : null;
    return {
      nome: MESES_CURTOS[m.mes - 1], meta, entregue: temRealizado ? soma : null, variacao,
      rotuloPct: variacao == null ? '' : `${variacao > 0 ? '+' : ''}${variacao.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`,
      ...Object.fromEntries(selecionados.map(s => [s, temRealizado ? (m.realizado_por_setor?.[s] ?? 0) : null])),
    };
  });
  const semMetaNaSelecao = !todosSelecionados && dadosSetor.every(d => d.meta == null);
  // Gráfico "Entregue × meta mensal": meses com realizado (os futuros ficam vazios).
  const dadosVariacao = dadosSetor.map(d => ({ nome: d.nome, meta: d.meta, entregue: d.entregue, variacao: d.variacao }));
  const extremos = dadosVariacao.map(d => d.variacao).filter((v): v is number => v != null);
  // Escala com marcas redondas: passo de 20/40/50% conforme a amplitude, folga para o rótulo.
  const minV = Math.min(0, ...extremos) - 8, maxV = Math.max(0, ...extremos) + 8;
  const passo = maxV - minV <= 100 ? 20 : maxV - minV <= 200 ? 40 : 50;
  const dominioVariacao: [number, number] = [Math.min(-passo, Math.floor(minV / passo) * passo), Math.max(passo, Math.ceil(maxV / passo) * passo)];
  const ticksVariacao: number[] = [];
  for (let t = dominioVariacao[0]; t <= dominioVariacao[1]; t += passo) ticksVariacao.push(t);

  // "Participação de cada setor": o ano inteiro ou a evolução mês a mês.
  const [participacaoVisao, setParticipacaoVisao] = useState<'ano' | 'mes'>('ano');

  const dadosAcum = (p?.acumulado || []).map(a => ({
    nome: MESES_CURTOS[a.mes - 1], meta: a.meta_acumulada, realizado: a.realizado_acumulado, projecao: a.projecao_acumulada ?? null,
  }));
  const dadosPrecisao = (p?.precisao || []).map(x => ({ nome: MESES_CURTOS[x.mes - 1], baseline: x.erro_baseline, ajuste: x.erro_ajuste }));

  const realizadoAno = p?.total.realizado ?? 0;
  const colunas: ColumnsType<Linha> = [
    {
      title: 'Mês/Trimestre', dataIndex: 'rotulo', fixed: 'left', width: 150, render: (_, l) => l.tipo === 'mes'
        ? <span>{MESES[(l as MesPainel).mes - 1]}{(l as MesPainel).origem === 'planilha' && <Tooltip title="Valor vindo do histórico da planilha"><Tag style={{ marginLeft: 6 }}>planilha</Tag></Tooltip>}</span>
        : <Text strong>{(l as Agregado).rotulo}</Text>,
    },
    { title: '(a) Baseline', dataIndex: 'baseline', align: 'right', render: v => brl(v) },
    { title: '(b) Ajuste', dataIndex: 'ajuste', align: 'right', render: v => brl(v) },
    { title: '(c) Meta', dataIndex: 'meta', align: 'right', render: v => brl(v) },
    { title: '(d) Realizado', dataIndex: 'realizado', align: 'right', render: v => <Text strong>{brl(v)}</Text> },
    ...setores.map(s => ({
      title: SETOR_CURTO[s], key: s, align: 'right' as const, render: (_: any, l: Linha) => {
        const v = l.realizado_por_setor?.[s];
        if (v == null) return '—';
        const tot = Object.values(l.realizado_por_setor || {}).reduce((a, b) => a + (b || 0), 0);
        return <span>{brl(v)}<br /><Text type="secondary" style={{ fontSize: 11 }}>{tot ? pct((v / tot) * 100) : '—'}</Text></span>;
      },
    })),
    { title: 'b × a', dataIndex: 'var_ajuste_baseline', align: 'right', render: v => <Var v={v} /> },
    { title: 'b × c', dataIndex: 'var_ajuste_meta', align: 'right', render: v => <Var v={v} /> },
    { title: 'd × b', dataIndex: 'var_realizado_ajuste', align: 'right', render: v => <Var v={v} /> },
    { title: 'd × c', dataIndex: 'var_realizado_meta', align: 'right', render: v => <Var v={v} /> },
  ];

  return (
    <Spin spinning={carregando}>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 12 }}>
        <Select style={{ width: 110 }} value={ano} onChange={setAno} showSearch optionFilterProp="label"
          options={[ano - 2, ano - 1, ano, ano + 1].map(a => ({ value: a, label: String(a) }))} />
        <Segmented value={visao} onChange={v => setVisao(v as any)} options={[{ value: 'graficos', label: 'Gráficos' }, { value: 'tabela', label: 'Tabela (Resumo)' }]} />
        {p && <Text type="secondary">Até {MESES[Number(p.inicio_controle.slice(5, 7)) - 2] || '—'}/{p.inicio_controle.slice(0, 4)}: histórico da planilha. Depois: sistema.</Text>}
      </Flex>
      {!p ? (!carregando && <Empty />) : (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Realizado no ano" value={brl(realizadoAno)} /></Card></Col>
            <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Meta anual · atingido" value={brl(p.meta_anual)} suffix={p.meta_anual ? <Text type="secondary" style={{ fontSize: 13 }}> · {pct((realizadoAno / p.meta_anual) * 100)}</Text> : null} /></Card></Col>
            <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Supermeta · atingido" value={brl(p.supermeta_anual)} suffix={p.supermeta_anual ? <Text type="secondary" style={{ fontSize: 13 }}> · {pct((realizadoAno / p.supermeta_anual) * 100)}</Text> : null} /></Card></Col>
            <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Média mensal" value={brl(p.media_mensal)} /></Card></Col>
            <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title={`Necessário/mês p/ meta (${p.necessario_por_mes.meses_restantes} meses)`} value={brl(p.necessario_por_mes.meta)} /></Card></Col>
            <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Necessário/mês p/ supermeta" value={brl(p.necessario_por_mes.supermeta)} /></Card></Col>
          </Row>
          {visao === 'tabela' ? (
            <Table<Linha> rowKey="chave" size="small" dataSource={linhas} columns={colunas} pagination={false} scroll={{ x: 1500 }}
              onRow={l => ({ style: l.tipo === 'mes' ? {} : { background: l.tipo === 'total' ? '#eef3fb' : '#fbf6ee' } })} />
          ) : (
            <Row gutter={[12, 12]}>
              <Col xs={24} xl={12}>
                <Card size="small" title="Mês a mês: baseline, ajuste e realizado contra a meta">
                  <div style={{ height: ALTURA }}>
                    <ResponsiveContainer>
                      <ComposedChart data={dadosMensais} barGap={2} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="#ecebe8" />
                        <XAxis dataKey="nome" tick={EIXO} axisLine={false} tickLine={false} />
                        <YAxis tick={EIXO} axisLine={false} tickLine={false} tickFormatter={brlCurto} width={78} />
                        <RTooltip formatter={(v: any) => brl(v)} />
                        <Legend />
                        <Bar dataKey="baseline" name="(a) Baseline" fill={SERIE_COR.baseline} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="ajuste" name="(b) Ajuste" fill={SERIE_COR.ajuste} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="realizado" name="(d) Realizado" fill={SERIE_COR.realizado} radius={[4, 4, 0, 0]} />
                        <Line dataKey="meta" name="(c) Meta" stroke={SERIE_COR.meta} strokeDasharray="5 4" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card size="small" title="Acumulado no ano contra a meta acumulada">
                  <div style={{ height: ALTURA }}>
                    <ResponsiveContainer>
                      <LineChart data={dadosAcum} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="#ecebe8" />
                        <XAxis dataKey="nome" tick={EIXO} axisLine={false} tickLine={false} />
                        <YAxis tick={EIXO} axisLine={false} tickLine={false} tickFormatter={brlCurto} width={78} />
                        <RTooltip formatter={(v: any) => brl(v)} />
                        <Legend />
                        <Line dataKey="meta" name="Meta acumulada" stroke={SERIE_COR.meta} strokeDasharray="5 4" strokeWidth={2} dot={false} />
                        <Line dataKey="realizado" name="Realizado acumulado" stroke={SERIE_COR.realizado} strokeWidth={2} dot={{ r: 4 }} connectNulls={false} />
                        <Line dataKey="projecao" name="Projeção (média dos meses fechados)" stroke={SERIE_COR.realizado} strokeDasharray="6 4" strokeWidth={2}
                          dot={{ r: 4, fill: '#fcfcfb', strokeWidth: 2 }} connectNulls={false} />
                        {p.meta_anual && <ReferenceLine y={p.meta_anual} stroke="#898781" strokeDasharray="2 4" label={{ value: 'meta anual', position: 'insideTopLeft', fill: '#898781', fontSize: 11 }} />}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {p.projecao?.media_mensal != null && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Projeção: média de {brl(p.projecao.media_mensal)}/mês dos {p.projecao.meses_na_media} meses fechados (o mês em andamento não entra),
                      somada a partir do mês atual. Fim do ano projetado: {brl(p.projecao.fim_do_ano)}
                      {p.meta_anual ? ` (${pct(((p.projecao.fim_do_ano || 0) / p.meta_anual) * 100)} da meta)` : ''}.
                    </Text>
                  )}
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card size="small" title="Realizado por setor (divisão do comercial/financeiro)"
                  extra={
                    <Select mode="multiple" allowClear size="small" style={{ minWidth: 220, maxWidth: 360 }} placeholder="Todos os setores"
                      showSearch optionFilterProp="label" maxTagCount="responsive" value={filtroSetores} onChange={setFiltroSetores}
                      options={setores.map(s => ({ value: s, label: SETOR_CURTO[s] }))} />
                  }>
                  <div style={{ height: ALTURA }}>
                    <ResponsiveContainer>
                      <ComposedChart data={dadosSetor} margin={{ top: 22, right: 8, left: 8, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="#ecebe8" />
                        <XAxis dataKey="nome" tick={EIXO} axisLine={false} tickLine={false} />
                        <YAxis tick={EIXO} axisLine={false} tickLine={false} tickFormatter={brlCurto} width={78} />
                        <RTooltip content={<TooltipSetor />} />
                        <Legend />
                        {selecionados.map((s, i) => (
                          <Bar key={s} dataKey={s} name={SETOR_CURTO[s]} stackId="s" fill={SETOR_COR[s]} stroke="#fcfcfb" strokeWidth={2}
                            radius={i === selecionados.length - 1 ? [4, 4, 0, 0] : 0} />
                        ))}
                        <Line dataKey="meta" name={todosSelecionados ? 'Meta mensal' : 'Meta mensal (setores escolhidos)'} stroke={SERIE_COR.meta}
                          strokeDasharray="5 4" strokeWidth={2} dot={false} connectNulls={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  {semMetaNaSelecao && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Os setores escolhidos não têm meta própria cadastrada (aba Metas e histórico): a linha de meta não aparece.
                    </Text>
                  )}
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                {/* Pedido do Gustavo (19/10/2026): a variação sobre a meta, num gráfico só dela.
                    A meta é a linha do zero (sem segundo eixo); o valor da meta vai embaixo do mês. */}
                <Card size="small" title={`Entregue × meta mensal${todosSelecionados ? '' : ' (setores escolhidos)'}`}>
                  {dadosVariacao.some(d => d.variacao != null) ? (
                    <div style={{ height: ALTURA }}>
                      <ResponsiveContainer>
                        <BarChart data={dadosVariacao} margin={{ top: 22, right: 8, left: 8, bottom: 8 }}>
                          <CartesianGrid vertical={false} stroke="#ecebe8" />
                          <XAxis dataKey="nome" axisLine={false} tickLine={false} interval={0} height={44} tick={<TickMesMeta dados={dadosVariacao} />} />
                          <YAxis tick={EIXO} axisLine={false} tickLine={false} width={52} domain={dominioVariacao} ticks={ticksVariacao}
                            tickFormatter={v => `${v > 0 ? '+' : ''}${v}%`} />
                          <RTooltip content={<TooltipVariacao />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                          {/* Rótulo da meta à direita: os meses do fim do ano estão vazios e ele não cobre barra. */}
                          <ReferenceLine y={0} stroke={SERIE_COR.meta} strokeDasharray="5 4" strokeWidth={2}
                            label={{ value: 'meta mensal', position: 'insideTopRight', fill: '#52514e', fontSize: 11 }} />
                          {/* minPointSize: variação quase zero (ex.: −0,02%) ainda desenha a barra e o rótulo. */}
                          <Bar dataKey="variacao" name="Variação sobre a meta" radius={[4, 4, 4, 4]} maxBarSize={36} minPointSize={2}>
                            {dadosVariacao.map(d => <Cell key={d.nome} fill={d.variacao != null && d.variacao >= 0 ? COR_ACIMA : COR_ABAIXO} />)}
                            <LabelList dataKey="variacao" content={<RotuloVariacao />} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Empty description={semMetaNaSelecao ? 'Sem meta para os setores escolhidos.' : 'Sem realizado com meta no ano.'} />
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Barra = quanto o entregue no mês ficou acima (verde) ou abaixo (vermelho) da meta do mês; a linha tracejada é a meta.
                    Embaixo de cada mês, o valor da meta. Segue o filtro de setores do gráfico ao lado.
                  </Text>
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card size="small" title="Participação de cada setor no realizado do ano"
                  extra={<Segmented size="small" value={participacaoVisao} onChange={v => setParticipacaoVisao(v as any)}
                    options={[{ value: 'ano', label: 'Ano' }, { value: 'mes', label: 'Mês a mês' }]} />}>
                  {participacaoVisao === 'mes' ? (
                    <div style={{ height: ALTURA }}>
                      <ResponsiveContainer>
                        <BarChart data={dadosMensais.filter((_, i) => p.meses[i].realizado != null)} stackOffset="expand" margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke="#ecebe8" />
                          <XAxis dataKey="nome" tick={EIXO} axisLine={false} tickLine={false} />
                          <YAxis tick={EIXO} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v * 100)}%`} width={44} />
                          <RTooltip formatter={(v: any, nome: any, item: any) => {
                            const tot = setores.reduce((a, s) => a + (item?.payload?.[s] || 0), 0);
                            return [`${brl(v)} · ${tot ? pct((v / tot) * 100) : '—'}`, nome];
                          }} />
                          <Legend />
                          {setores.map((s, i) => (
                            <Bar key={s} dataKey={s} name={SETOR_CURTO[s]} stackId="p" fill={SETOR_COR[s]} stroke="#fcfcfb" strokeWidth={2}
                              radius={i === setores.length - 1 ? [4, 4, 0, 0] : 0} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                  <Flex vertical gap={10} style={{ padding: '4px 4px 0' }}>
                    {setores.map(s => {
                      const v = p.total.realizado_por_setor?.[s] ?? 0;
                      const part = p.participacao_setor?.[s] ?? 0;
                      return (
                        <Tooltip key={s} title={`${SETOR_NOMES[s]}: ${brl(v)} (${pct(part)})`}>
                          <div>
                            <Flex justify="space-between"><Text>{SETOR_NOMES[s]}</Text><Text>{brl(v)} · {pct(part)}</Text></Flex>
                            <div style={{ background: '#f0efec', borderRadius: 4, height: 10 }}>
                              <div style={{ width: `${Math.min(100, part)}%`, background: SETOR_COR[s], height: 10, borderRadius: 4 }} />
                            </div>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </Flex>
                  )}
                  <div style={{ marginTop: 16 }}>
                    <Text strong>Precisão da previsão</Text> <Text type="secondary">(quanto o realizado ficou acima/abaixo do previsto)</Text>
                    <div style={{ height: 180 }}>
                      <ResponsiveContainer>
                        <BarChart data={dadosPrecisao} barGap={2} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke="#ecebe8" />
                          <XAxis dataKey="nome" tick={EIXO} axisLine={false} tickLine={false} />
                          <YAxis tick={EIXO} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} width={48} />
                          <ReferenceLine y={0} stroke="#898781" />
                          <RTooltip formatter={(v: any) => pct(v, 2)} />
                          <Legend />
                          <Bar dataKey="baseline" name="Realizado × baseline" fill={SERIE_COR.baseline} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="ajuste" name="Realizado × ajuste" fill={SERIE_COR.ajuste} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Spin>
  );
}
